import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

export type UserRole = 'commissioner' | 'player';

interface RoleContextType {
  role: UserRole;
  isCommissioner: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  user: User | null;
  toggleRole: () => void;
  setRole: (role: UserRole) => void;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

// Map database roles to app roles
const mapDbRoleToAppRole = (dbRole: string | null): UserRole => {
  // In the database, 'admin' or 'moderator' would be treated as commissioner
  // 'user' would be treated as player
  if (dbRole === 'admin' || dbRole === 'moderator') {
    return 'commissioner';
  }
  return 'player';
};

export const RoleProvider = ({ children }: { children: ReactNode }) => {
  const [role, setRole] = useState<UserRole>('player');
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Fetch user role from database
  const fetchUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user role:', error);
        return 'player' as UserRole;
      }

      // Map the database role to app role
      return mapDbRoleToAppRole(data?.role || null);
    } catch (err) {
      console.error('Error in fetchUserRole:', err);
      return 'player' as UserRole;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        setIsAuthenticated(!!session?.user);
        
        // Defer role fetching to avoid deadlock
        if (session?.user) {
          setTimeout(async () => {
            const userRole = await fetchUserRole(session.user.id);
            setRole(userRole);
            setIsLoading(false);
          }, 0);
        } else {
          // Not authenticated - default to player role for demo mode
          setRole('commissioner'); // Demo mode default
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setUser(session?.user ?? null);
      setIsAuthenticated(!!session?.user);
      
      if (session?.user) {
        const userRole = await fetchUserRole(session.user.id);
        setRole(userRole);
      } else {
        // Not authenticated - allow demo mode with commissioner by default
        setRole('commissioner');
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Toggle role - always allow toggling for demo/testing purposes
  const toggleRole = useCallback(() => {
    setRole(prev => prev === 'commissioner' ? 'player' : 'commissioner');
  }, []);

  // Direct role setter - respects authentication
  const handleSetRole = useCallback((newRole: UserRole) => {
    if (!isAuthenticated) {
      setRole(newRole);
    } else {
      console.warn('Cannot manually set role when authenticated. Role is determined by database.');
    }
  }, [isAuthenticated]);

  const value = useMemo(() => ({
    role,
    isCommissioner: role === 'commissioner',
    isLoading,
    isAuthenticated,
    user,
    toggleRole,
    setRole: handleSetRole,
  }), [role, isLoading, isAuthenticated, user, toggleRole, handleSetRole]);

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};
